import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  LayoutDashboard,
  Users,
  FileText,
  Stethoscope,
  Heart,
  Calendar,
  Archive,
  Settings,
  Menu,
  X,
  LogOut,
  User,
  Shield,
  ShieldCheck,
  Building2,
  AlertTriangle,
  Sun,
  Moon,
  HelpCircle,
  Globe,
  Clock,
  Sparkles,
  Database,
  MessageSquare,
  BookOpen,
  Mail,
} from 'lucide-react'
import { supabase } from '../services/supabase'
import { useTranslation } from 'react-i18next'
import { useOrganization } from '../contexts/OrganizationContext'
import { useMasterAdmin } from '../contexts/MasterAdminContext'
import { useTheme } from '../contexts/ThemeContext'
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus'
import { useRoutePersistence } from '../hooks/useRoutePersistence'
import { NAV_ITEMS } from '../utils/constants'
import TermsAcceptanceModal from './TermsAcceptanceModal'
import { checkTermsAcceptance, checkPrivacyAcceptance } from '../services/legalDocuments'
import LanguageSwitcher from './LanguageSwitcher'
import AppHelpGuide from './AppHelpGuide'

const iconMap = {
      LayoutDashboard,
      Users,
      FileText,
      Stethoscope,
      Heart,
      Calendar,
      Archive,
      Settings,
      Shield,
      ShieldCheck,
      AlertTriangle,
      HelpCircle,
      Globe,
      MessageSquare,
      BookOpen,
      Mail,
    }

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showHelpGuide, setShowHelpGuide] = useState(false)
  const location = useLocation()
  const { organization } = useOrganization()
  const [showTermsModal, setShowTermsModal] = useState(false)
  const [userId, setUserId] = useState(null)
  const [checkingTerms, setCheckingTerms] = useState(true)

  // Use route persistence hook to save and restore routes
  useRoutePersistence()

  // Check terms/privacy acceptance on mount
  useEffect(() => {
    const checkAcceptance = async () => {
      // Don't show modal if user is on legal documents pages (they need to read first)
      if (location.pathname.startsWith('/legal/')) {
        setCheckingTerms(false)
        return
      }

      if (!organization?.id) {
        setCheckingTerms(false)
        return
      }

      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user?.id) {
          setCheckingTerms(false)
          return
        }

        setUserId(user.id)

        // Check if user has accepted terms and privacy
        const termsResult = await checkTermsAcceptance(user.id, organization.id)
        const privacyResult = await checkPrivacyAcceptance(user.id, organization.id)

        // Show modal if either is not accepted
        if (!termsResult.success || !termsResult.data || !privacyResult.success || !privacyResult.data) {
          setShowTermsModal(true)
        }
      } catch (error) {
        console.error('Error checking terms acceptance:', error)
      } finally {
        setCheckingTerms(false)
      }
    }

    checkAcceptance()
  }, [organization?.id, location.pathname])

  const handleLogout = async () => {
    await supabase.auth.signOut()
  }

  const isActive = (path) => location.pathname === path

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 w-full max-w-full min-w-0 overflow-x-auto">
      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-50 glass-card mx-4 mt-4 mb-4">
        <div className="flex items-center justify-between p-4">
          <h1 className="text-xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 dark:from-primary-400 dark:to-secondary-400 bg-clip-text text-transparent">
            NaturAssist
          </h1>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-xl bg-primary-100 dark:bg-gray-700 text-primary-600 dark:text-primary-400 hover:bg-primary-200 dark:hover:bg-gray-600 transition-colors"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSidebarOpen(false)}
              className="fixed inset-0 bg-black/20 dark:bg-black/40 backdrop-blur-sm z-40 lg:hidden"
            />
            <motion.div
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed left-0 top-0 bottom-0 w-72 glass-card z-50 lg:hidden shadow-2xl overflow-hidden flex flex-col h-screen max-h-screen"
            >
              <SidebarContent
                isActive={isActive}
                onNavigate={() => setSidebarOpen(false)}
                onLogout={handleLogout}
                onOpenHelpGuide={() => setShowHelpGuide(true)}
                isMobile={true}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <div className="flex min-w-0 w-full max-w-full">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:block w-72 glass-card m-4 sticky top-4 self-start max-h-[calc(100vh-2rem)] flex flex-col flex-shrink-0">
          <SidebarContent
            isActive={isActive}
            onNavigate={() => {}}
            onLogout={handleLogout}
            onOpenHelpGuide={() => setShowHelpGuide(true)}
          />
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-h-screen pb-20 lg:pb-0 min-w-0 w-full max-w-full overflow-x-auto">
          {children}
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav isActive={isActive} />

      {/* Terms Acceptance Modal - Don't show on legal document pages */}
      {showTermsModal && organization?.id && userId && !checkingTerms && !location.pathname.startsWith('/legal/') && (
        <TermsAcceptanceModal
          organizationId={organization.id}
          userId={userId}
          onAccept={() => {
            setShowTermsModal(false)
          }}
        />
      )}

      {/* Help Guide Modal */}
      {showHelpGuide && (
        <AppHelpGuide onClose={() => setShowHelpGuide(false)} />
      )}
    </div>
  )
}

const SidebarContent = ({ isActive, onNavigate, onLogout, onOpenHelpGuide, isMobile = false }) => {
  const { t } = useTranslation()
  const { isAdmin, canRead, canWrite, organization } = useOrganization()
  const { isMasterAdmin } = useMasterAdmin()
  const { isTrial, daysLeft } = useSubscriptionStatus()

  // Map navigation items with translations
  // Helper function to convert path to translation key
  const pathToTranslationKey = (path) => {
    const pathMap = {
      '/dashboard': 'dashboard',
      '/clients': 'clients',
      '/examination': 'examination',
      '/care-plans': 'carePlans',
      '/therapies': 'therapies',
      '/sessions': 'sessions',
      '/organization': 'organization',
      '/backup-restore': 'backuprestore',
      '/support': 'support',
      '/forum': 'forum',
      '/invitations': 'invitations',
    }
    return pathMap[path] || path.replace('/', '').replace(/-/g, '')
  }

  const translatedNavItems = NAV_ITEMS.map((item) => ({
    ...item,
    label: t(`navigation.${pathToTranslationKey(item.path)}`) || item.label,
  }))

  const filteredNavItems = translatedNavItems.filter((item) => {
    if (!item.permission) return true // Dashboard always visible
    if (item.permission.requireAdmin) return isAdmin || false
    if (item.permission.resource) {
      if (item.permission.action === 'write') {
        return canWrite ? canWrite(item.permission.resource) : false
      }
      return canRead ? canRead(item.permission.resource) : false
    }
    return true
  })

  // Add Error Logs to navigation if master admin
  const errorLogsItem = isMasterAdmin
    ? [{ path: '/errors', label: t('navigation.errorLogs'), icon: AlertTriangle, permission: null }]
    : []

  const masterAdminNavItems = [
    { path: '/master-admin', label: t('navigation.masterAdmin'), icon: Shield },
    { path: '/master-admin/organizations', label: t('navigation.organizations'), icon: Building2 },
    { path: '/master-admin/organization-requests', label: t('navigation.orgRequests'), icon: FileText },
    { path: '/master-admin/storage-limits', label: t('navigation.storageLimits'), icon: Database },
    { path: '/gdpr', label: t('navigation.gdprManagement'), icon: ShieldCheck },
  ]

  return (
    <div className={`p-6 ${isMobile ? 'h-screen max-h-screen' : 'h-full'} flex flex-col min-h-0 overflow-hidden`}>
      {/* Logo */}
      <div className="mb-4 flex-shrink-0">
        <h1 className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
          NaturAssist
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {organization?.name || t('layout.tagline', 'Naturopathic Care')}
        </p>
      </div>

      {/* Scrollable Content Area - Navigation and bottom sections together */}
      <div className="flex-1 overflow-y-auto min-h-0 pr-2 -mr-2 sidebar-scroll overscroll-contain flex flex-col">
        {/* Navigation */}
        <nav className="space-y-2 flex-shrink-0">
          {filteredNavItems.map((item) => {
            const Icon = iconMap[item.icon]
            if (!Icon) return null // Skip if icon is not found
            const active = isActive(item.path)
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onNavigate}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  active
                    ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-primary-50 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400'
                }`}
              >
                <Icon size={20} />
                <span className="font-medium">{item.label}</span>
              </Link>
            )
          })}

          {/* Error Logs for Master Admins */}
          {errorLogsItem.map((item) => {
            const Icon = iconMap[item.icon]
            if (!Icon) return null // Skip if icon is not found
            const active = isActive(item.path)
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onNavigate}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  active
                    ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-red-50 dark:hover:bg-gray-700 hover:text-red-600 dark:hover:text-red-400'
                }`}
              >
                <Icon size={20} />
                <span className="font-medium">{item.label}</span>
              </Link>
            )
          })}

          {/* Master Admin Navigation */}
          {isMasterAdmin && (
            <>
              <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  {t('navigation.platformAdmin')}
                </p>
              </div>
              {masterAdminNavItems.map((item) => {
                const Icon = item.icon
                if (!Icon) return null // Skip if icon is not found
                const active = isActive(item.path)
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={onNavigate}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                      active
                        ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-lg'
                        : 'text-gray-700 dark:text-gray-200 hover:bg-purple-50 dark:hover:bg-gray-700 hover:text-purple-600 dark:hover:text-purple-400'
                    }`}
                  >
                    <Icon size={20} />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                )
              })}
            </>
          )}
        </nav>

        {/* Bottom Section - Part of scrollable content */}
        <div className="flex-shrink-0 flex flex-col mt-4">
        {/* Trial Countdown Banner */}
        {isTrial && daysLeft !== null && (
          <div className={`${isMobile ? 'pt-3' : 'pt-6'} border-t border-gray-200 dark:border-gray-700`}>
            <Link
              to="/organization"
              onClick={onNavigate}
              className="block p-4 rounded-xl bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border border-yellow-200 dark:border-yellow-800 mb-4 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-yellow-400 to-orange-400 flex items-center justify-center flex-shrink-0">
                  <Clock size={20} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-yellow-900 dark:text-yellow-200 mb-1">
                    {t('trial.title')}
                  </p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mb-2">
                    {daysLeft === 0 
                      ? t('trial.endsToday') 
                      : daysLeft === 1 
                      ? t('trial.dayLeft') 
                      : t('trial.daysLeft', { count: daysLeft })}
                  </p>
                  <p className="text-xs font-medium text-yellow-800 dark:text-yellow-200 flex items-center gap-1">
                    <Sparkles size={12} />
                    {t('trial.upgrade')}
                  </p>
                </div>
              </div>
            </Link>
          </div>
        )}

        {/* Theme Toggle */}
        <div className={`${isMobile ? 'pt-3' : 'pt-6'} border-t border-gray-200 dark:border-gray-700`}>
          <ThemeToggle />
        </div>

        {/* Language Switcher */}
        <div className={`${isMobile ? 'pt-3' : 'pt-6'} border-t border-gray-200 dark:border-gray-700`}>
          <LanguageSwitcher />
        </div>

        {/* User Section */}
        <div className={`${isMobile ? 'pt-3' : 'pt-6'} border-t border-gray-200 dark:border-gray-700`}>
        <Link
          to="/profile"
          onClick={onNavigate}
          className={`flex items-center gap-3 px-4 py-3 mb-3 rounded-xl transition-all duration-200 ${
            isActive('/profile')
              ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg'
              : 'bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-gray-700 dark:to-gray-800 text-gray-700 dark:text-gray-200 hover:bg-primary-100 dark:hover:bg-gray-700'
          }`}
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-primary-400 to-secondary-400 flex items-center justify-center">
            <User size={20} className="text-white" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold">{t('navigation.profile')}</p>
            <p className="text-xs opacity-80">{t('profile.accountSettings')}</p>
          </div>
        </Link>
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-700 dark:text-gray-200 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-all duration-200"
        >
          <LogOut size={20} />
          <span className="font-medium">{t('navigation.logout')}</span>
        </button>
        <button
          onClick={() => (onOpenHelpGuide ? onOpenHelpGuide() : null)}
          className="w-full flex items-center gap-3 px-4 py-3 mb-3 rounded-xl text-gray-700 dark:text-gray-200 hover:bg-primary-50 dark:hover:bg-gray-700 transition-all duration-200"
        >
          <BookOpen size={20} />
          <span className="font-medium">{t('navigation.userGuide')}</span>
        </button>
        </div>
      </div>
      </div>
    </div>
  )
}

// Theme Toggle Component
const ThemeToggle = () => {
  const { t } = useTranslation()
  const { isDark, toggleTheme } = useTheme()

  return (
    <button
      onClick={toggleTheme}
      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-700 dark:text-gray-200 hover:bg-primary-50 dark:hover:bg-gray-700 transition-all duration-200"
      aria-label={isDark ? t('theme.switchToLight', 'Switch to light mode') : t('theme.switchToDark', 'Switch to dark mode')}
    >
      {isDark ? (
        <>
          <Sun size={20} />
          <span className="font-medium">{t('theme.lightMode')}</span>
        </>
      ) : (
        <>
          <Moon size={20} />
          <span className="font-medium">{t('theme.darkMode')}</span>
        </>
      )}
    </button>
  )
}

// Mobile Bottom Navigation Component
const MobileBottomNav = ({ isActive }) => {
  const { t } = useTranslation()
  const { canRead } = useOrganization()

  // Quick navigation items (always visible, permission checked)
  const quickNavItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('navigation.dashboard'), permission: null },
    { path: '/clients', icon: Users, label: t('navigation.clients'), permission: { resource: 'clients', action: 'read' } },
    { path: '/examination', icon: Stethoscope, label: t('navigation.examination'), permission: { resource: 'clients', action: 'write' } },
    { path: '/care-plans', icon: Heart, label: t('navigation.carePlans'), permission: { resource: 'care_plans', action: 'read' } },
    { path: '/sessions', icon: Archive, label: t('navigation.sessions'), permission: { resource: 'sessions', action: 'read' } },
  ]

  // Filter items based on permissions
  const visibleItems = quickNavItems.filter((item) => {
    if (!item.permission) return true
    if (item.permission.resource && canRead) {
      return canRead(item.permission.resource)
    }
    return true
  })

  return (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md border-t border-gray-200 dark:border-gray-700 shadow-lg safe-area-bottom">
      <div className="flex items-center justify-around px-2 py-2 pb-safe">
        {visibleItems.map((item) => (
          <MobileNavButton
            key={item.path}
            to={item.path}
            icon={item.icon}
            label={item.label}
            isActive={isActive(item.path)}
          />
        ))}
      </div>
    </div>
  )
}

// Mobile Bottom Navigation Button
const MobileNavButton = ({ to, icon: Icon, label, isActive }) => {
  if (!Icon) return null // Skip if icon is not provided
  return (
    <Link
      to={to}
      className={`flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-xl transition-all duration-200 min-w-[60px] ${
        isActive
          ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20'
          : 'text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400'
      }`}
    >
      <Icon size={22} className={isActive ? 'text-primary-600 dark:text-primary-400' : ''} />
      <span className={`text-xs font-medium ${isActive ? 'text-primary-600 dark:text-primary-400' : ''}`}>
        {label}
      </span>
    </Link>
  )
}

export default Layout

